'use client';

import React from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
} from '@west-tennessee-consulting/ui/components/dropdown-menu';
import { MoreHorizontal, Pencil, Star, StarOff, FolderInput, RotateCcw, Info, Trash2 } from 'lucide-react';
import type { ChatProject } from '../hooks/useChatOrganization.js';

const NONE_VALUE = '__none__';

export interface ChatItemMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isStarred: boolean;
  isArchived: boolean;
  projects: ChatProject[];
  currentProjectId: string | null;
  onRename: () => void;
  onToggleStar: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
  onShowDetails: () => void;
  onAssignProject: (projectId: string | null) => void;
  triggerClassName: string;
}

/**
 * The per-chat kebab menu. Also serves as the row's right-click menu: the row
 * opens this same controlled menu via `onOpenChange`.
 */
export function ChatItemMenu({
  open,
  onOpenChange,
  isStarred,
  isArchived,
  projects,
  currentProjectId,
  onRename,
  onToggleStar,
  onDelete,
  onRestore,
  onShowDetails,
  onAssignProject,
  triggerClassName,
}: ChatItemMenuProps) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={triggerClassName}
          aria-label="Chat actions"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={onRename}>
          <Pencil className="mr-2 h-4 w-4" aria-hidden />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onToggleStar}>
          {isStarred ? (
            <StarOff className="mr-2 h-4 w-4" aria-hidden />
          ) : (
            <Star className="mr-2 h-4 w-4" aria-hidden />
          )}
          {isStarred ? 'Unstar' : 'Star'}
        </DropdownMenuItem>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <FolderInput className="mr-2 h-4 w-4" aria-hidden />
            Move to project
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-52">
            {projects.length === 0 ? (
              <DropdownMenuLabel className="font-normal text-muted-foreground">
                No projects yet
              </DropdownMenuLabel>
            ) : (
              <DropdownMenuRadioGroup
                value={currentProjectId ?? NONE_VALUE}
                onValueChange={(value) =>
                  onAssignProject(value === NONE_VALUE ? null : value)
                }
              >
                <DropdownMenuRadioItem value={NONE_VALUE}>None</DropdownMenuRadioItem>
                {projects.map((project) => (
                  <DropdownMenuRadioItem key={project.id} value={project.id}>
                    <span className="truncate">{project.name}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {isArchived && onRestore && (
          <DropdownMenuItem onSelect={onRestore}>
            <RotateCcw className="mr-2 h-4 w-4" aria-hidden />
            Restore
          </DropdownMenuItem>
        )}

        <DropdownMenuItem onSelect={onShowDetails}>
          <Info className="mr-2 h-4 w-4" aria-hidden />
          Details
        </DropdownMenuItem>

        {onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
